/**
 * Clinics Modal Component V3
 *
 * Modal for creating and editing clinics with TIME-WINDOWED device associations.
 * Supports creating associations with start_timestamp and end_timestamp.
 */

import { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Alert, Badge, Card, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import RefreshButton from '@/components/common/RefreshButton';
import TimeWindowTimeline from '@/components/common/TimeWindowTimeline';
import CSVUploadButton from '../CSVUploadButton';
import type { Clinic, Device } from '@/types/associations';

interface DeviceAssociation {
  device_id: number;
  start_timestamp?: string;
  end_timestamp?: string;
}

interface ClinicsModalProps {
  show: boolean;
  clinic?: Clinic | null;
  devices?: Device[];
  onHide: () => void;
  onSave: (data: Partial<Clinic>) => Promise<void>;
  onRefresh?: () => Promise<void>;
}

export default function ClinicsModalV3({ show, clinic, devices = [], onHide, onSave, onRefresh }: ClinicsModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    clinic_id: '',
    description: '',
    location: '',
  });

  const [deviceAssociations, setDeviceAssociations] = useState<DeviceAssociation[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [newAssociation, setNewAssociation] = useState({
    start_timestamp: '',
    end_timestamp: '',
  });

  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (clinic) {
      setFormData({
        name: clinic.name || '',
        clinic_id: clinic.clinic_id || '',
        description: clinic.description || '',
        location: clinic.location || '',
      });
      // Load existing device associations if available
      // Note: This assumes the API returns device associations with time-windows
      setDeviceAssociations([]);
    } else {
      setFormData({
        name: '',
        clinic_id: '',
        description: '',
        location: '',
      });
      setDeviceAssociations([]);
    }
    setSelectedDeviceId('');
    setNewAssociation({ start_timestamp: '', end_timestamp: '' });
    setError(null);
  }, [clinic, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      await onSave({
        ...formData,
        device_associations: deviceAssociations,
      } as Partial<Clinic>);
      onHide();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.policyDatasets.saveFailed', { type: 'clinic' }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddDeviceAssociation = () => {
    if (!selectedDeviceId) {
      setError(t('admin.policyDatasets.selectDeviceFirst'));
      return;
    }

    const deviceId = parseInt(selectedDeviceId, 10);

    // Check if device already associated
    if (deviceAssociations.some((a) => a.device_id === deviceId)) {
      setError(t('admin.policyDatasets.deviceAlreadyAssociated'));
      return;
    }

    // Validate time-window
    if (newAssociation.start_timestamp && newAssociation.end_timestamp) {
      const start = new Date(newAssociation.start_timestamp);
      const end = new Date(newAssociation.end_timestamp);
      if (start >= end) {
        setError(t('admin.policyDatasets.endMustBeAfterStart'));
        return;
      }
    }

    const association: DeviceAssociation = {
      device_id: deviceId,
      start_timestamp: newAssociation.start_timestamp || undefined,
      end_timestamp: newAssociation.end_timestamp || undefined,
    };

    setDeviceAssociations([...deviceAssociations, association]);
    setSelectedDeviceId('');
    setNewAssociation({ start_timestamp: '', end_timestamp: '' });
    setError(null);
  };

  const handleRemoveDeviceAssociation = (deviceId: number) => {
    setDeviceAssociations(deviceAssociations.filter((a) => a.device_id !== deviceId));
  };

  const getDeviceName = (deviceId: number): string => {
    const device = devices.find((d) => d.id === deviceId);
    return device ? device.device_id + (device.description ? ` (${device.description})` : '') : `Device ${deviceId}`;
  };

  // Prepare timeline data
  // All windows are for the same clinic, so we only need to check if same device has overlapping windows
  const timelineWindows = useMemo(() => {
    const clinicId = clinic?.id;
    return deviceAssociations
      .filter((a) => a.start_timestamp && a.end_timestamp)
      .map((a) => ({
        id: a.device_id,
        label: getDeviceName(a.device_id),
        start: new Date(a.start_timestamp!),
        end: new Date(a.end_timestamp!),
        entity1: clinicId, // clinic_id (all same, so overlap detection will work on entity2)
        entity2: a.device_id, // device_id - overlaps only when same device has multiple windows
      }));
  }, [deviceAssociations, devices, clinic]);

  return (
    <Modal show={show} onHide={onHide} centered size="xl" scrollable>
      <Modal.Header closeButton>
        <div className="d-flex justify-content-between align-items-center w-100 me-3">
          <Modal.Title>{clinic ? t('admin.policyDatasets.editClinic') : t('admin.policyDatasets.createClinic')}</Modal.Title>
          {onRefresh && <RefreshButton onRefresh={onRefresh} size="sm" variant="outline-secondary" />}
        </div>
      </Modal.Header>
      <Modal.Body>
        <Form id="clinic-form" onSubmit={handleSubmit}>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Basic Information */}
          <h6 className="mb-3">{t('admin.policyDatasets.basicInformation')}</h6>

          <Form.Group className="mb-3">
            <Form.Label>
              {t('admin.policyDatasets.name')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('admin.policyDatasets.namePlaceholder', { example: 'Main Hospital' })}
              required
              autoFocus
            />
            <Form.Text className="text-muted">{t('admin.policyDatasets.nameRequired', { type: 'clinic' })}</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              {t('admin.policyDatasets.clinicId')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.clinic_id}
              onChange={(e) => setFormData({ ...formData, clinic_id: e.target.value })}
              placeholder={t('admin.policyDatasets.clinicIdPlaceholder')}
              required
              disabled={!!clinic}
            />
            <Form.Text className="text-muted">
              {clinic ? t('admin.policyDatasets.clinicIdCannotChange') : t('admin.policyDatasets.clinicIdRequired')}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>{t('admin.policyDatasets.description')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t('admin.policyDatasets.descriptionPlaceholder')}
            />
            <Form.Text className="text-muted">{t('admin.policyDatasets.descriptionOptional')}</Form.Text>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>{t('admin.policyDatasets.location')}</Form.Label>
            <Form.Control
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder={t('admin.policyDatasets.locationPlaceholder')}
            />
            <Form.Text className="text-muted">{t('admin.policyDatasets.locationOptional')}</Form.Text>
          </Form.Group>

          <hr />

          {/* Device Associations with Time-Windows */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">
              {t('admin.policyDatasets.deviceAssociations')}
              <Badge bg="secondary" className="ms-2">
                {deviceAssociations.length}
              </Badge>
            </h6>
            {clinic && (
              <CSVUploadButton
                clinicId={clinic.id}
                onUploadComplete={() => {
                  // Refresh the page to show new associations
                  if (onRefresh) {
                    onRefresh();
                  }
                }}
                size="sm"
              />
            )}
          </div>

          {devices.length === 0 ? (
            <Alert variant="info">{t('admin.policyDatasets.noDevicesAvailable')}</Alert>
          ) : (
            <>
              {/* Add Device Association Form */}
              <Card className="mb-3 bg-light">
                <Card.Body>
                  <h6 className="mb-3">{t('admin.policyDatasets.addDeviceAssociation')}</h6>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('admin.policyDatasets.devices')}</Form.Label>
                        <Form.Select
                          value={selectedDeviceId}
                          onChange={(e) => setSelectedDeviceId(e.target.value)}
                        >
                          <option value="">{t('admin.policyDatasets.selectDevice')}</option>
                          {devices
                            .filter((d) => !deviceAssociations.some((a) => a.device_id === d.id))
                            .map((device) => (
                              <option key={device.id} value={device.id}>
                                {device.device_id} {device.description && `(${device.description})`}
                              </option>
                            ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('admin.policyDatasets.startTimestamp')}</Form.Label>
                        <Form.Control
                          type="datetime-local"
                          value={newAssociation.start_timestamp}
                          onChange={(e) => setNewAssociation({ ...newAssociation, start_timestamp: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('admin.policyDatasets.endTimestamp')}</Form.Label>
                        <Form.Control
                          type="datetime-local"
                          value={newAssociation.end_timestamp}
                          onChange={(e) => setNewAssociation({ ...newAssociation, end_timestamp: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Label className="d-block">&nbsp;</Form.Label>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={handleAddDeviceAssociation}
                        disabled={!selectedDeviceId}
                        className="w-100"
                      >
                        {t('admin.policyDatasets.add')}
                      </Button>
                    </Col>
                  </Row>
                  <Form.Text className="text-muted">
                    {t('admin.policyDatasets.leaveTimeWindowsEmpty', { type: 'device', parent: 'clinic' })}
                  </Form.Text>
                </Card.Body>
              </Card>

              {/* Timeline Visualization */}
              {timelineWindows.length > 0 && (
                <div className="mb-3">
                  <h6 className="mb-2">{t('admin.policyDatasets.timeWindowVisualization')}</h6>
                  <TimeWindowTimeline windows={timelineWindows} highlightOverlaps={true} height={150} />
                </div>
              )}

              {/* Existing Device Associations List */}
              {deviceAssociations.length > 0 && (
                <div className="mb-3">
                  <h6 className="mb-2">{t('admin.policyDatasets.associatedDevices')}</h6>
                  {deviceAssociations.map((assoc) => (
                    <Card key={assoc.device_id} className="mb-2">
                      <Card.Body className="py-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{getDeviceName(assoc.device_id)}</strong>
                            {assoc.start_timestamp || assoc.end_timestamp ? (
                              <div className="text-muted small mt-1">
                                {assoc.start_timestamp ? (
                                  <span>
                                    {t('admin.policyDatasets.from')}: {new Date(assoc.start_timestamp).toLocaleString()}
                                  </span>
                                ) : (
                                  <span>{t('admin.policyDatasets.from')}: {t('admin.policyDatasets.unlimited')}</span>
                                )}
                                {' → '}
                                {assoc.end_timestamp ? (
                                  <span>{t('admin.policyDatasets.to')}: {new Date(assoc.end_timestamp).toLocaleString()}</span>
                                ) : (
                                  <span>{t('admin.policyDatasets.to')}: {t('admin.policyDatasets.unlimited')}</span>
                                )}
                              </div>
                            ) : (
                              <div className="text-muted small mt-1">{t('admin.policyDatasets.noTimeWindow')}</div>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleRemoveDeviceAssociation(assoc.device_id)}
                          >
                            {t('admin.policyDatasets.remove')}
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}

              {deviceAssociations.length === 0 && (
                <Alert variant="info">
                  {t('admin.policyDatasets.noDeviceAssociations')}
                </Alert>
              )}
            </>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSaving}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" type="submit" form="clinic-form" disabled={isSaving}>
          {isSaving ? t('admin.policyDatasets.saving') : clinic ? t('common.update') : t('common.create')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
